import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, MapPin, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { ROOMS_ROUTE, CONTACT, SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

const CONTACT_CARDS = [
  {
    icon: Phone,
    titleEs: 'Llámenos',
    titleEn: 'Call Us',
    bodyEs: 'El soporte telefónico está disponible las 24hs. No dude en llamarnos.',
    bodyEn: 'Phone support is available 24 hours. Do not hesitate to call us.',
    valueEs: '+504 3264 9600',
    valueEn: '+504 3264 9600',
    href: 'tel:+50432649600',
  },
  {
    icon: Clock,
    titleEs: 'Horarios',
    titleEn: 'Hours',
    bodyEs: 'Nuestra recepción está abierta las 24 horas del día, los 365 días del año.',
    bodyEn: 'Our reception is open 24 hours a day, 365 days a year.',
    valueEs: 'Abierto las 24 hs',
    valueEn: 'Open 24 hours',
    href: undefined,
  },
  {
    icon: Mail,
    titleEs: 'Escríbanos',
    titleEn: 'Write to Us',
    bodyEs: 'Para consultas y preguntas generales, contáctenos por correo electrónico.',
    bodyEn: 'For inquiries and general questions, contact us by email.',
    valueEs: 'reserva@hbnuevavalladolid.com',
    valueEn: 'reserva@hbnuevavalladolid.com',
    href: 'mailto:reserva@hbnuevavalladolid.com',
  },
  {
    icon: MapPin,
    titleEs: 'Visítenos',
    titleEn: 'Visit Us',
    bodyEs: 'Comayagua, Barrio Abajo, 12101, Honduras',
    bodyEn: 'Comayagua, Barrio Abajo, 12101, Honduras',
    valueEs: undefined,
    valueEn: undefined,
    href: CONTACT.googleMapsUrl,
  },
]

export function ContactPage() {
  const { i18n } = useTranslation()
  const es = !i18n.language.startsWith('en')

  const [values, setValues] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    mensaje: '',
  })
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const subject = encodeURIComponent(
      es
        ? `Consulta desde el sitio web — ${values.nombre} ${values.apellido}`
        : `Website inquiry — ${values.nombre} ${values.apellido}`,
    )
    const body = encodeURIComponent(
      [
        `${values.nombre} ${values.apellido}`.trim(),
        values.telefono ? values.telefono : '',
        '',
        values.mensaje,
        '',
        `— ${values.email}`,
      ]
        .filter(Boolean)
        .join('\n'),
    )
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`
    setSubmitted(true)
  }

  return (
    <>
      <Helmet>
        <title>{`${es ? 'Contacto' : 'Contact'} | ${SITE_NAME}`}</title>
        <meta
          name="description"
          content={
            es
              ? 'Contáctenos en Hotel Boutique Nueva Valladolid. Teléfono: +504 3264 9600. Email: reserva@hbnuevavalladolid.com. Barrio Abajo, Comayagua, Honduras.'
              : 'Contact us at Hotel Boutique Nueva Valladolid. Phone: +504 3264 9600. Email: reserva@hbnuevavalladolid.com. Barrio Abajo, Comayagua, Honduras.'
          }
        />
      </Helmet>

      {/* ── HERO PARALLAX ── */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${getImageUrl('/images/nuva/exterior/exterior-catedral-comayagua-bg.jpg')})`,
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '18rem',
          paddingBottom: '10rem',
        }}
      >
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 px-4 text-center">
          <h1
            className="text-6xl font-normal text-white"
            style={{ fontFamily: 'VeniceBridge, serif', textShadow: '0 0 15px rgba(0,0,0,0.8)' }}
          >
            {es ? 'Contacto' : 'Contact'}
          </h1>
          <h2
            className="text-3xl font-bold uppercase tracking-wide text-[#F3EADB]"
            style={{ marginTop: '-8px' }}
          >
            {SITE_NAME}
          </h2>
        </div>
      </section>

      {/* ── 4 CONTACT INFO CARDS ── */}
      <section className="bg-white py-24 px-10">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {CONTACT_CARDS.map((card, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="flex flex-col items-center text-center px-2">
                  <div
                    className="mb-5 flex items-center justify-center rounded-full"
                    style={{
                      width: '60px',
                      height: '60px',
                      border: '1px solid #0F2966',
                      flexShrink: 0,
                    }}
                  >
                    <card.icon className="h-6 w-6 text-[#0F2966]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-bold uppercase tracking-wide text-[#0F2966] mb-3">
                    {es ? card.titleEs : card.titleEn}
                  </h3>
                  <p className="text-sm text-[#666666] leading-relaxed mb-3 max-w-[200px]">
                    {es ? card.bodyEs : card.bodyEn}
                  </p>
                  {(es ? card.valueEs : card.valueEn) && (
                    card.href ? (
                      <a
                        href={card.href}
                        target={card.href.startsWith('http') ? '_blank' : undefined}
                        rel={card.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-sm font-semibold text-[#0F2966] hover:underline break-all"
                      >
                        {es ? card.valueEs : card.valueEn}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-[#0F2966]">
                        {es ? card.valueEs : card.valueEn}
                      </p>
                    )
                  )}
                  {card.icon === MapPin && (
                    <a
                      href={CONTACT.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-[#0F2966] hover:underline"
                    >
                      {es ? 'Comayagua, Barrio Abajo, 12101, Honduras' : 'Comayagua, Barrio Abajo, 12101, Honduras'}
                    </a>
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT FORM ── */}
      <section className="bg-[#F3EADB] py-24 px-10">
        <div className="mx-auto max-w-4xl">
          <AnimatedSection>
            <h2 className="text-center text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-12">
              {es ? 'Envíenos un Mensaje' : 'Send Us a Message'}
            </h2>

            {submitted ? (
              <div className="text-center py-16">
                <p className="text-xl font-semibold text-[#0F2966] mb-2">
                  {es
                    ? 'Gracias por ponerse en contacto con nosotros.'
                    : 'Thank you for contacting us.'}
                </p>
                <p className="text-base text-[#666666]">
                  {es
                    ? 'Nos pondremos en contacto con usted lo antes posible.'
                    : 'We will contact you as soon as possible.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="nombre"
                      className="mb-2 block text-sm font-semibold uppercase tracking-wide text-[#0F2966]"
                    >
                      {es ? 'Nombre' : 'First Name'}
                    </label>
                    <input
                      id="nombre"
                      type="text"
                      required
                      value={values.nombre}
                      onChange={(e) => setValues({ ...values, nombre: e.target.value })}
                      placeholder={es ? 'Su nombre' : 'Your first name'}
                      className="w-full border border-[#EEE7E0] bg-white px-4 py-3 text-sm text-[#333] placeholder-[#aaa] focus:outline-none focus:border-[#0F2966] transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="apellido"
                      className="mb-2 block text-sm font-semibold uppercase tracking-wide text-[#0F2966]"
                    >
                      {es ? 'Apellido' : 'Last Name'}
                    </label>
                    <input
                      id="apellido"
                      type="text"
                      required
                      value={values.apellido}
                      onChange={(e) => setValues({ ...values, apellido: e.target.value })}
                      placeholder={es ? 'Su apellido' : 'Your last name'}
                      className="w-full border border-[#EEE7E0] bg-white px-4 py-3 text-sm text-[#333] placeholder-[#aaa] focus:outline-none focus:border-[#0F2966] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-semibold uppercase tracking-wide text-[#0F2966]"
                    >
                      {es ? 'Email' : 'Email'}
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={values.email}
                      onChange={(e) => setValues({ ...values, email: e.target.value })}
                      placeholder={es ? 'Su correo electrónico' : 'Your email address'}
                      className="w-full border border-[#EEE7E0] bg-white px-4 py-3 text-sm text-[#333] placeholder-[#aaa] focus:outline-none focus:border-[#0F2966] transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="telefono"
                      className="mb-2 block text-sm font-semibold uppercase tracking-wide text-[#0F2966]"
                    >
                      {es ? 'Teléfono' : 'Phone'}
                    </label>
                    <input
                      id="telefono"
                      type="tel"
                      value={values.telefono}
                      onChange={(e) => setValues({ ...values, telefono: e.target.value })}
                      placeholder={es ? 'Su teléfono' : 'Your phone number'}
                      className="w-full border border-[#EEE7E0] bg-white px-4 py-3 text-sm text-[#333] placeholder-[#aaa] focus:outline-none focus:border-[#0F2966] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="mensaje"
                    className="mb-2 block text-sm font-semibold uppercase tracking-wide text-[#0F2966]"
                  >
                    {es ? 'Mensaje' : 'Message'}
                  </label>
                  <textarea
                    id="mensaje"
                    required
                    rows={6}
                    value={values.mensaje}
                    onChange={(e) => setValues({ ...values, mensaje: e.target.value })}
                    placeholder={es ? 'Su mensaje...' : 'Your message...'}
                    className="w-full border border-[#EEE7E0] bg-white px-4 py-3 text-sm text-[#333] placeholder-[#aaa] focus:outline-none focus:border-[#0F2966] transition-colors resize-none"
                  />
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-full bg-[#0F2966] text-white text-sm font-bold uppercase tracking-wide hover:bg-[#F3EADB] hover:text-[#0F2966] transition-colors border border-[#0F2966]"
                    style={{ width: '200px', height: '50px' }}
                  >
                    {es ? 'Enviar' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* ── MAP ── */}
      <section className="bg-white py-0">
        <div className="w-full">
          <iframe
            src={CONTACT.googleMapsEmbed}
            width="100%"
            height="450"
            style={{ border: 0, display: 'block' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={es ? 'Ubicación Hotel Boutique Nueva Valladolid, Comayagua' : 'Location Hotel Boutique Nueva Valladolid, Comayagua'}
          />
        </div>
      </section>

      {/* ── BOOKING CTA ── */}
      <section className="bg-[#0F2966] py-24 px-10">
        <div className="mx-auto max-w-7xl text-center">
          <AnimatedSection>
            <h2 className="text-3xl font-bold uppercase tracking-wide text-white mb-4 md:text-4xl">
              {es
                ? 'NUEVA VALLADOLID es tu Hotel perfecto en Comayagua'
                : 'NUEVA VALLADOLID is your perfect Hotel in Comayagua'}
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-base text-white/85 leading-relaxed">
              {es
                ? 'Reserve directamente y obtenga el mejor precio. Recepción disponible las 24 horas para atender cualquier consulta sobre su estadía.'
                : 'Book directly and get the best price. Reception available 24 hours to answer any questions about your stay.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link
                to={ROOMS_ROUTE}
                className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-white text-sm font-bold uppercase tracking-wide hover:bg-[#F3EADB] hover:text-[#0F2966] transition-colors"
                style={{ width: '200px', height: '50px' }}
              >
                {es ? 'Reservar Ahora' : 'Book Now'}
              </Link>
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-white text-sm font-bold uppercase tracking-wide hover:bg-[#F3EADB] hover:text-[#0F2966] transition-colors"
                style={{ width: '200px', height: '50px' }}
              >
                {es ? 'Llamar Ahora' : 'Call Now'}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
